import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useSEO } from '../hooks/useSEO';

import begravning1 from '../begravning-1-begravningskrans.png';
import begravning2 from '../begravning-2-vita-rosor.png';
import begravning3 from '../begravning-3-kistdekoration.png';
import begravning4 from '../begravning-4-bukett-pastellfarger.png';
import begravning5 from '../begravning-5-minnesgavla.png';
import begravning6 from '../begravning-6-hjarta-arrangement.png';
import begravning7 from '../begravning-7-rosa-nejlikor.png';
import begravning8 from '../begravning-8-elegant-bukett.png';
import begravning9 from '../begravning-9-konstgjorda-blommor.png';
import begravning10 from '../begravning-10-gravdekoration.png';
import begravning11 from '../begravning-11-stor-krans.png';
import begravning12 from '../begravning-12-blommande-arrangemang.png';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
  background: linear-gradient(180deg, #fefcf8 0%, #f8efe6 46%, #faf3ec 100%);
  padding-bottom: clamp(80px, 14vw, 160px);
  overflow-x: hidden;
`;

const HeroBackdrop = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(96px, 14vw, 170px) 0 clamp(80px, 10vw, 140px);
  background:
    radial-gradient(circle at 12% 18%, rgba(108, 129, 117, 0.08), transparent 58%),
    radial-gradient(circle at 82% 12%, rgba(215, 186, 155, 0.17), transparent 58%),
    radial-gradient(circle at 18% 88%, rgba(171, 146, 128, 0.12), transparent 54%),
    linear-gradient(165deg, #fefbf7 0%, #f8eee4 45%, #fbf4ed 100%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(182deg, rgba(255, 254, 252, 0.5) 0%, rgba(255, 255, 255, 0) 22%),
      radial-gradient(circle at 6% 93%, rgba(208, 184, 161, 0.08) 0%, transparent 52%),
      radial-gradient(circle at 94% 4%, rgba(168, 180, 168, 0.05) 0%, transparent 48%);
  }

  @media (max-width: 768px) {
    padding: 96px clamp(16px, 5vw, 28px) 72px;
  }
`;

const HeroShell = styled.div`
  width: min(1120px, 100%);
  margin: 0 auto;
  padding: 0 clamp(12px, 5vw, 28px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(32px, 6vw, 56px);
  box-sizing: border-box;

  @media (max-width: 767px) {
    padding: 0 clamp(12px, 4vw, 18px);

    & > * {
      width: 100%;
      max-width: min(360px, 100%);
      margin-left: auto;
      margin-right: auto;
    }
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: clamp(56px, 7vw, 110px);
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(24px, 3.8vw, 36px);
  width: 100%;
  max-width: min(360px, 100%);
  text-align: center;
  z-index: 2;
  animation: ${fadeUp} 520ms ease-out 90ms both;
  align-items: center;
  padding: 0;

  @media (min-width: 768px) {
    max-width: min(440px, 100%);
  }

  @media (min-width: 1024px) {
    max-width: 520px;
    text-align: left;
    align-items: flex-start;
    padding-right: clamp(32px, 3.8vw, 48px);
  }
`;

const HeroTag = styled.span`
  width: fit-content;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(70, 83, 75, 0.1);
  color: rgba(45, 56, 50, 0.8);
  font-weight: 600;
  font-size: clamp(0.82rem, 0.9vw, 0.92rem);
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 1023px) {
    margin: 0 auto;
    text-align: center;
  }

  @media (min-width: 1024px) {
    text-align: left;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.32rem, 3vw, 2.94rem);
  font-weight: 630;
  color: #2b2725;
  line-height: clamp(1.1, 1.35vw, 1.16);
  letter-spacing: -0.008em;
  max-width: 22ch;

  @media (max-width: 768px) {
    max-width: 20ch;
    margin: 0 auto;
  }

  @media (min-width: 1024px) {
    margin: 0;
  }
`;

const HeroText = styled.p`
  font-size: clamp(1.05rem, 2.1vw, 1.28rem);
  line-height: 1.92;
  color: rgba(43, 41, 39, 0.72);
  max-width: 520px;
  word-break: break-word;

  @media (max-width: 1023px) {
    margin: 0 auto;
    text-align: center;
  }

  @media (min-width: 1024px) {
    text-align: left;
  }
`;

const HeroHighlights = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(16px, 4vw, 22px);
  width: 100%;
  max-width: min(360px, 100%);
  margin: 0 auto;
  padding: 0 clamp(12px, 5vw, 24px);
  animation: ${fadeUp} 520ms ease-out 160ms both;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    justify-items: stretch;
    max-width: 520px;
    padding: 0 clamp(18px, 4vw, 24px);
  }

  @media (min-width: 1024px) {
    max-width: 520px;
    padding: 0;
  }
`;

const HighlightCard = styled.div`
  padding: clamp(16px, 3vw, 22px);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(222, 210, 198, 0.34);
  box-shadow: 0 12px 28px rgba(40, 35, 30, 0.07);
  display: grid;
  gap: 6px;
  width: auto;
  max-width: 320px;
  margin: 0 auto;
  text-align: center;
  align-self: center;

  @media (min-width: 1024px) {
    margin: 0;
    max-width: none;
    text-align: left;
  }
`;

const HighlightTitle = styled.span`
  font-weight: 600;
  color: #2e3933;
  font-size: 1rem;
`;

const HighlightDescription = styled.span`
  color: rgba(46, 57, 51, 0.7);
  font-size: 0.94rem;
  line-height: 1.6;
`;

const HeroMedia = styled.div`
  position: relative;
  display: grid;
  gap: clamp(20px, 4vw, 32px);
  justify-items: center;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  align-self: center;
  padding: 0 clamp(12px, 4vw, 20px);
  animation: ${fadeUp} 560ms ease-out 210ms both;

  @media (min-width: 1024px) {
    justify-items: stretch;
    margin: 0;
    align-self: stretch;
  }
`;

const HeroPrimary = styled.div`
  position: relative;
  border-radius: clamp(26px, 4vw, 44px);
  overflow: hidden;
  box-shadow: 0 28px 52px rgba(40, 35, 30, 0.14);
  background: linear-gradient(140deg, rgba(41, 48, 44, 0.66), rgba(120, 103, 88, 0.32));
  min-height: clamp(240px, 64vw, 420px);
  width: 100%;
  max-width: 100%;
  justify-self: stretch;

  @media (max-width: 1023px) {
    margin: 0 auto;
  }
`;

const HeroPrimaryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  mix-blend-mode: lighten;
`;

const HeroSecondary = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(20px, 3.6vw, 28px);
  width: 100%;
  padding: clamp(20px, 3.4vw, 26px);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(248, 244, 238, 0.92), rgba(255, 255, 255, 0.94));
  border: 1px solid rgba(220, 211, 201, 0.34);
  box-shadow: 0 12px 28px rgba(40, 35, 30, 0.07);
  align-self: center;
  justify-self: stretch;

  @media (max-width: 1023px) {
    flex-direction: column;
    text-align: center;
    gap: clamp(14px, 4vw, 20px);
  }
`;

const HeroSecondaryImage = styled.img`
  width: clamp(64px, 14vw, 96px);
  height: clamp(64px, 14vw, 96px);
  border-radius: 18px;
  object-fit: cover;
  box-shadow: 0 12px 24px rgba(32, 28, 25, 0.12);
`;

const HeroSecondaryContent = styled.div`
  display: grid;
  gap: clamp(4px, 1vw, 10px);

  @media (max-width: 1023px) {
    text-align: center;
  }
`;

const HeroSecondaryTitle = styled.span`
  font-weight: 600;
  font-size: clamp(1rem, 2vw, 1.06rem);
  color: #2f3a34;
`;

const HeroSecondaryText = styled.p`
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.64;
  color: rgba(47, 58, 52, 0.7);
`;

const HeroBadge = styled.div`
  position: absolute;
  top: clamp(20px, 3vw, 28px);
  left: clamp(20px, 3vw, 32px);
  padding: clamp(14px, 2.5vw, 18px) clamp(18px, 3vw, 22px);
  border-radius: 22px;
  display: grid;
  gap: 6px;
  color: #2c2a29;
  background: rgba(248, 243, 237, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(214, 205, 196, 0.6);
  box-shadow: 0 12px 28px rgba(40, 35, 30, 0.14);
  max-width: clamp(200px, 70%, 240px);

  span:first-child {
    font-size: 0.76rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(44, 42, 41, 0.62);
  }

  span:last-child {
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.5;
  }

  @media (max-width: 1023px) {
    position: static;
    margin: clamp(16px, 5vw, 24px) auto 0;
    width: 100%;
    max-width: clamp(240px, 88vw, 320px);
    text-align: center;
    word-break: break-word;
  }
`;

const SectionSpacing = styled.section`
  margin-top: clamp(70px, 9vw, 110px);
`;

const SectionIntro = styled.div`
  display: grid;
  gap: clamp(18px, 3vw, 26px);
  max-width: 780px;
  margin-bottom: clamp(36px, 5vw, 54px);

  @media (max-width: 768px) {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    max-width: min(360px, 100%);
  }
`;

const SectionEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(47, 58, 52, 0.7);

  &::before {
    content: '';
    display: inline-block;
    width: 34px;
    height: 1px;
    background: rgba(47, 58, 52, 0.4);
  }

  @media (max-width: 768px) {
    justify-content: center;
    margin: 0 auto;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 3vw, 2.6rem);
  font-weight: 600;
  color: #2f3a34;
  letter-spacing: -0.01em;
  margin: 0 auto;

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const SectionText = styled.p`
  font-size: clamp(1rem, 1.9vw, 1.14rem);
  line-height: 1.8;
  color: rgba(44, 42, 41, 0.72);

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(22px, 5vw, 32px);
  justify-items: center;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    justify-items: stretch;
  }
`;

const ServiceCard = styled.article<{ accent?: string }>`
  position: relative;
  border-radius: 28px;
  background: ${({ accent }) =>
    accent
      ? `linear-gradient(140deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.85) 38%, ${accent})`
      : 'rgba(255, 255, 255, 0.9)'};
  box-shadow: 0 30px 58px rgba(44, 35, 28, 0.12);
  border: 1px solid rgba(222, 211, 198, 0.4);
  overflow: hidden;
  display: grid;
  gap: 22px;
  padding: clamp(24px, 3vw, 32px);
  width: auto;
  max-width: 340px;
  margin: 0 auto;
  align-self: center;

  @media (max-width: 600px) {
    padding: 22px;
  }
`;

const ServiceImage = styled.img`
  width: 100%;
  height: clamp(160px, 22vw, 200px);
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(30, 27, 24, 0.18);
`;

const ServiceTitle = styled.h3`
  font-size: clamp(1.3rem, 2.2vw, 1.55rem);
  font-weight: 600;
  color: #2c2a29;
`;

const ServiceDescription = styled.p`
  color: rgba(44, 42, 41, 0.72);
  line-height: 1.7;
  font-size: 1rem;
`;

const ServiceDetails = styled.span`
  font-size: 0.95rem;
  color: rgba(47, 58, 52, 0.65);
`;

const StoryBlock = styled.div`
  position: relative;
  border-radius: 32px;
  padding: clamp(32px, 5vw, 48px);
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 36px 64px rgba(38, 33, 29, 0.14);
  border: 1px solid rgba(223, 214, 203, 0.45);
  display: grid;
  gap: clamp(20px, 3vw, 28px);

  @media (max-width: 768px) {
    padding: clamp(26px, 6vw, 32px);
  }
`;

const QuoteCard = styled.div`
  display: grid;
  gap: 12px;
  padding: clamp(24px, 3.5vw, 32px);
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(95, 109, 98, 0.12), rgba(169, 143, 118, 0.22));
  border: 1px solid rgba(214, 200, 186, 0.5);
  color: #2f3a34;
  font-size: 1rem;
  line-height: 1.7;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: clamp(22px, 6vw, 28px);
  }
`;

const GalleryWrapper = styled.div`
  position: relative;
  border-radius: 38px;
  padding: clamp(30px, 4vw, 46px);
  background: linear-gradient(150deg, rgba(43, 50, 45, 0.08), rgba(200, 174, 144, 0.12));
  box-shadow: inset 0 0 0 1px rgba(214, 203, 192, 0.4);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: clamp(24px, 6vw, 32px);
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(18px, 3vw, 26px);
  justify-items: center;

  @media (min-width: 600px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
`;

const GalleryItem = styled.div`
  position: relative;
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 26px 46px rgba(31, 27, 22, 0.18);
  transition: transform 0.28s ease, box-shadow 0.28s ease;
  background: rgba(255, 255, 255, 0.85);
  width: 100%;
  max-width: 420px;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 36px 64px rgba(31, 27, 22, 0.25);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: clamp(180px, 22vw, 240px);
  object-fit: cover;
  transition: transform 0.3s ease;

  ${GalleryItem}:hover & {
    transform: scale(1.04);
  }

  @media (max-width: 600px) {
    height: clamp(160px, 50vw, 220px);
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(20px, 4vw, 30px);
  justify-items: center;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    justify-items: stretch;
  }
`;

const ProcessCard = styled.div`
  position: relative;
  border-radius: 26px;
  padding: clamp(26px, 4vw, 34px);
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(221, 209, 196, 0.5);
  box-shadow: 0 28px 56px rgba(38, 32, 27, 0.12);
  display: grid;
  gap: 14px;
  width: auto;
  max-width: 340px;
  margin: 0 auto;
  align-self: center;

  @media (max-width: 600px) {
    padding: 22px;
  }
`;

const ProcessBadge = styled.span`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(47, 58, 52, 0.85), rgba(26, 29, 27, 0.95));
  color: #fff;
  font-weight: 600;
`;

const ProcessTitle = styled.span`
  font-size: 1.12rem;
  font-weight: 600;
  color: #2f3a34;
`;

const ProcessText = styled.p`
  font-size: 0.98rem;
  line-height: 1.7;
  color: rgba(47, 58, 52, 0.7);
`;

const ContactSection = styled.section`
  margin-top: clamp(90px, 12vw, 150px);
  position: relative;
  overflow: hidden;
  padding: clamp(70px, 10vw, 110px) 0;
  background: radial-gradient(circle at 15% 20%, rgba(95, 109, 98, 0.12), transparent 55%),
    radial-gradient(circle at 85% 80%, rgba(209, 178, 149, 0.18), transparent 60%),
    linear-gradient(160deg, #2f3a34 0%, #1f2823 100%);
  color: #fff;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

const ContactCard = styled.div`
  display: grid;
  justify-items: center;
  text-align: center;
  gap: clamp(20px, 3vw, 28px);
  width: min(620px, 100%);
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: clamp(18px, 6vw, 24px);
    padding: 0 16px;
  }
`;

const ContactHeading = styled.h2`
  font-size: clamp(2rem, 3.2vw, 2.6rem);
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const ContactText = styled.p`
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.82);

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 36px;
  border-radius: 999px;
  background: #f8f3ed;
  color: #2f3a34;
  font-weight: 600;
  text-decoration: none;
  font-size: 1.06rem;
  box-shadow: 0 24px 46px rgba(10, 14, 12, 0.22);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 30px 56px rgba(10, 14, 12, 0.3);
    background: #ffffff;
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const ContactLink = styled.a`
  color: rgba(255, 255, 255, 0.82);
  font-size: 1.02rem;
  font-weight: 500;
  text-decoration: none;
  transition: opacity 0.18s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const highlightItems = [
  {
    title: 'Omsorgsfullt hantverk',
    description: 'Varje arrangemang binds i vår ateljé i Kramfors med omsorg om detaljerna.'
  },
  {
    title: 'Personlig guidning',
    description: 'Vi lyssnar in berättelserna ni vill förmedla och formar blommorna därefter.'
  },
  {
    title: 'Leverans & ceremonistöd',
    description: 'Vi koordinerar leverans till kyrka, kapell eller minnesstund runt Höga Kusten.'
  }
];

const processSteps = [
  {
    step: '1',
    title: 'Samtal & önskemål',
    text: 'Vi börjar med ett samtal, möte i butiken eller ett mail där vi hjälper med vad ni vill ha för typ utav blommor eller dekoration eller att ni själva framför era önskemål.'
  },
  {
    step: '2',
    title: 'Skiss & materialval',
    text: 'Tillsammans arbetar vi fram en harmonisk färgpalett och väljer blommor som håller sig vackra genom ceremonin.'
  },
  {
    step: '3',
    title: 'Bindning & leverans',
    text: 'Vi binder allt för hand, förbereder band och kort och ser till att arrangemangen levereras i rätt tid.'
  }
];

const funeralPieces = [
  {
    id: 'krans',
    src: begravning1,
    title: 'Begravningskrans',
    description: 'Traditionell krans i vita och gröna toner',
    accent: 'linear-gradient(160deg, rgba(121, 132, 123, 0.18), rgba(255, 255, 255, 0))'
  },
  {
    id: 'vita-rosor',
    src: begravning2,
    title: 'Vita rosor arrangement',
    description: 'Elegant bukett med vita rosor och grönt',
    accent: 'linear-gradient(160deg, rgba(224, 212, 198, 0.22), rgba(255, 255, 255, 0))'
  },
  {
    id: 'kistdekoration',
    src: begravning3,
    title: 'Kistdekoration',
    description: 'Vacker dekoration för kista i pastellfärger',
    accent: 'linear-gradient(160deg, rgba(207, 178, 149, 0.2), rgba(255, 255, 255, 0))'
  },
  {
    id: 'pastellbukett',
    src: begravning4,
    title: 'Bukett i pastellfärger',
    description: 'Mjuk bukett i rosa och vita toner',
    accent: 'linear-gradient(160deg, rgba(202, 173, 185, 0.2), rgba(255, 255, 255, 0))'
  },
  {
    id: 'minnesgavla',
    src: begravning5,
    title: 'Minnesgåvla',
    description: 'Personlig gåvla till minne av den avlidne',
    accent: 'linear-gradient(160deg, rgba(164, 174, 169, 0.22), rgba(255, 255, 255, 0))'
  },
  {
    id: 'hjarta',
    src: begravning6,
    title: 'Hjärta-arrangement',
    description: 'Hjärtformad dekoration i kärleksfulla färger',
    accent: 'linear-gradient(160deg, rgba(229, 177, 165, 0.24), rgba(255, 255, 255, 0))'
  },
  {
    id: 'nejlikor',
    src: begravning7,
    title: 'Rosa nejlikor',
    description: 'Klassisk bukett med rosa nejlikor',
    accent: 'linear-gradient(160deg, rgba(237, 196, 210, 0.24), rgba(255, 255, 255, 0))'
  },
  {
    id: 'elegant-bukett',
    src: begravning8,
    title: 'Elegant bukett',
    description: 'Sofistikerad bukett för begravning',
    accent: 'linear-gradient(160deg, rgba(183, 171, 162, 0.22), rgba(255, 255, 255, 0))'
  },
  {
    id: 'konstgjort',
    src: begravning9,
    title: 'Konstgjorda blommor',
    description: 'Hållbara konstgjorda blommor för graven',
    accent: 'linear-gradient(160deg, rgba(149, 159, 150, 0.24), rgba(255, 255, 255, 0))'
  },
  {
    id: 'gravdekoration',
    src: begravning10,
    title: 'Gravdekoration',
    description: 'Permanent dekoration för gravplatsen',
    accent: 'linear-gradient(160deg, rgba(140, 124, 112, 0.26), rgba(255, 255, 255, 0))'
  },
  {
    id: 'stor-krans',
    src: begravning11,
    title: 'Stor begravningskrans',
    description: 'Imponerande krans för ceremonin',
    accent: 'linear-gradient(160deg, rgba(167, 189, 173, 0.22), rgba(255, 255, 255, 0))'
  },
  {
    id: 'blommande',
    src: begravning12,
    title: 'Blommande arrangemang',
    description: 'Färgstarkt arrangemang för att hedra minnet',
    accent: 'linear-gradient(160deg, rgba(206, 170, 167, 0.26), rgba(255, 255, 255, 0))'
  }
];

const serviceSpotlightIds = ['kistdekoration', 'stor-krans', 'pastellbukett'];

const Begravningar: React.FC = () => {
  useSEO({
    entityType: 'page',
    entityId: 'begravningar',
    fallbackTitle: 'Begravningsblommor i Kramfors – Lilla Flora',
    fallbackDescription:
      'Omsorgsfullt arrangerade begravningsblommor från Lilla Flora i Kramfors. Vi skapar kransar, kistdekorationer och buketter som hedrar minnet med värme.',
    fallbackImage: '/static/bild2.png'
  });

  const serviceSpotlights = useMemo(
    () => funeralPieces.filter(piece => serviceSpotlightIds.includes(piece.id)),
    []
  );

  const galleryItems = useMemo(() => funeralPieces, []);

  return (
    <PageWrapper>
      <HeroBackdrop>
        <HeroShell>
          <HeroContent>
            <HeroTag>Avsked med omtanke</HeroTag>
            <HeroTitle>Blommor som bär minnen</HeroTitle>
            <HeroText>
              På Lilla Flora är vi stolta över att få skapa vackra och personliga blomsterarrangemang
              för begravningar. Med lyhördhet för era önskemål binder vi buketter, kransar och
              kistdekorationer som uttrycker kärlek, omtanke och respekt.
            </HeroText>
            <HeroHighlights>
              {highlightItems.map(item => (
                <HighlightCard key={item.title}>
                  <HighlightTitle>{item.title}</HighlightTitle>
                  <HighlightDescription>{item.description}</HighlightDescription>
                </HighlightCard>
              ))}
            </HeroHighlights>
          </HeroContent>

          <HeroMedia>
            <HeroPrimary>
              <HeroPrimaryImage src={begravning3} alt="Kistdekoration i mjuka pastelltoner" />
              <HeroBadge>
                <span>Lilla Floras ateljé</span>
                <span>Personliga arrangemang bundna med omtanke i Kramfors.</span>
              </HeroBadge>
            </HeroPrimary>
            <HeroSecondary>
              <HeroSecondaryImage src={begravning6} alt="Hjärtformad dekoration i rosa toner" />
              <HeroSecondaryContent>
                <HeroSecondaryTitle>Omsorg i varje steg</HeroSecondaryTitle>
                <HeroSecondaryText>
                  Vi guidar er genom färgval, texter och leverans så att allt känns harmoniskt under ceremonin.
                </HeroSecondaryText>
              </HeroSecondaryContent>
            </HeroSecondary>
          </HeroMedia>
        </HeroShell>
      </HeroBackdrop>

      <div className="container">
        <SectionSpacing>
          <SectionIntro>
            <SectionEyebrow>Vi hjälper er hela vägen</SectionEyebrow>
            <SectionTitle>Skapa en harmonisk helhet för ceremonin</SectionTitle>
            <SectionText>
              När ni vänder er till oss får ni ett lugnt och tryggt stöd genom hela processen.
              Tillsammans formar vi en röd tråd för ceremonin – från kistdekoration och kransar
              till handbuketter och minnesgåvor.
            </SectionText>
          </SectionIntro>

          <ServicesGrid>
            {serviceSpotlights.map(service => (
              <ServiceCard key={service.id} accent={service.accent}>
                <ServiceImage src={service.src} alt={service.title} />
                <div>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </div>
                <ServiceDetails>
                  Vi anpassar form, storlek och text på bandet efter önskemål – från klassiskt
                  stilrent till mjukt blommande uttryck.
                </ServiceDetails>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </SectionSpacing>

        <SectionSpacing>
          <StoryBlock>
            <SectionTitle>Blommor som speglar personen ni minns</SectionTitle>
            <SectionText>
              Vi förstår att detta är en sårbar stund. Därför arbetar vi nära er och tar oss tid
              att lyssna. Färg, form, personliga detaljer – allt får ta plats för att skapa ett
              arrangemang som känns äkta och sant mot den ni vill hedra. Många väljer att väva in
              ett särskilt minne, en älskad färg eller blommor från barndomen.
            </SectionText>
            <SectionText>
              Här nedan delar vi några av våra senaste arbeten. Låt dem inspirera och väck tankar
              om vad som skulle kunna spegla er berättelse.
            </SectionText>
            <QuoteCard>
              <strong>“Det viktigaste är att det känns rätt för er.”</strong>
              <span>
                Vi guidar er gärna i valet av blommor, texter och band – antingen i butiken eller
                via telefon om det är lättare så.
              </span>
            </QuoteCard>
          </StoryBlock>
        </SectionSpacing>

        <SectionSpacing>
          <SectionIntro>
            <SectionEyebrow>Inspiration</SectionEyebrow>
            <SectionTitle>Vårt urval</SectionTitle>
            <SectionText>
              Mjuka pasteller, stillsam grönska eller naturtrogna konstblommor som klarar många
              dagar. Vi hjälper er att hitta rätt uttryck.
            </SectionText>
          </SectionIntro>

          <GalleryWrapper>
            <GalleryGrid>
              {galleryItems.map(item => (
                <GalleryItem key={item.id}>
                  <GalleryImage src={item.src} alt={item.title} />
                </GalleryItem>
              ))}
            </GalleryGrid>
          </GalleryWrapper>
        </SectionSpacing>

        <SectionSpacing>
          <SectionIntro>
            <SectionEyebrow>Så går det till</SectionEyebrow>
            <SectionTitle>Tryggt stöd från första samtal till ceremonin</SectionTitle>
            <SectionText>
              Vi möter er i butik eller via telefon – oavsett hur ni vill planera är vårt mål att
              skapa en varm och omtänksam upplevelse.
            </SectionText>
          </SectionIntro>

          <ProcessGrid>
            {processSteps.map(item => (
              <ProcessCard key={item.step}>
                <ProcessBadge>{item.step}</ProcessBadge>
                <ProcessTitle>{item.title}</ProcessTitle>
                <ProcessText>{item.text}</ProcessText>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </SectionSpacing>
      </div>

      <ContactSection>
        <div className="container">
          <ContactCard>
            <ContactHeading>Välkommen att kontakta oss</ContactHeading>
            <ContactText>
              Tillsammans skapar vi blomsterarrangemang som känns personliga och varsamma. Hör av
              dig så bokar vi in ett samtal eller ett besök i butiken.
            </ContactText>
            <ContactButton to="/kontakt">📞 Kontakta oss</ContactButton>
            <ContactLink href="tel:0708555052">Ring oss: 070-855 50 52</ContactLink>
          </ContactCard>
        </div>
      </ContactSection>
    </PageWrapper>
  );
};

export default Begravningar;
